import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { gridUnit, masksDefaultHeight, pageHeight, pageWidth } from '../../constants/gridConfig'
import { useStore } from '../../store/store';
import { getContainerById, getMaskById, getColumnById, getBoxesByColumnId } from '../../store/lookups';
import './Container.less'
import { borderColumnColor, BOX_CLICK_SELECTOR, containerBackground, ItemTypes, minContainerHeight, pageBackground, viewerTypes } from '../../constants/constants';
import RearangeColumnsButton from './RearangeColumnsButton';
import { useOutsideAlerter } from '../../hooks';
import ColumnWrapper from '../Column/ColumnWrapper';
import EditableContainerButton from './EditableContainerButton';
import NoRepeatContainerButton from './NoRepeatContainerButton';
import { useOverlay, leftShownStatus } from '../../contexts/OverlayContext';
import { isADrawBox } from '../../utils/boxes';
import { previewContainerLockHolder } from '../../collab/structureLocks';
import { LockedBoxOverlay } from '../../collab/LockedBoxOverlay';
import FloatyStylingWrapper from '../Table/FloatyStylingWrapper';
import BoxCopyDeleteButtons from '../common/BoxCopyDeleteButtons';
import { Separator } from '../Layout/Separator';
import { computeClampedFloatyPosition } from '../../utils/styles';
import { containerColumnGap, getTopLevelContainer } from '../../utils/columns';

const selector = (id, fromViewer) => ({
  configuration,
  containers,
  rowContainers,
  columns,
  maskEditing,
  masks,
  boxes,
  landscape,
  fromPdf,
  currentDocument,
  selectedContainer,
  selectedContainerIds,
  activeContainerId,
  handleUnSelectBox,
  handleSelectContainer,
  handleUnSelectContainer,
  boxVariableSelectedId,
  zoomLevel,
  boxLocks,
  user
}) => {
  // Aperçu : le service PDF aplatit les lignes principales d'une colonne seule
  // en containers frères (cf. flattenMainRows), donc les pages d'aperçu
  // désignent ces lignes par leur propre id. Une ligne se rend exactement comme
  // un container — ses sous-colonnes prennent la largeur qu'occupait la colonne
  // porteuse — il suffit d'aller la chercher là où elle vit.
  const container =
    getContainerById(containers, id) ??
    getContainerById(rowContainers ?? [], id);
  // Aperçu de section par un co-éditeur (dérivé) : étiquette + contour au
  // niveau container, pas par box. Jamais dans l'aperçu/PDF (vue propre).
  const previewLock =
    fromViewer || fromPdf
      ? null
      : previewContainerLockHolder(
        { boxLocks, boxes, containers, columns, masks, user },
        id
      );
  const mask = !container && (fromViewer || fromPdf || (maskEditing.length > 0
    && maskEditing.includes(id)))
    && getMaskById(masks, id);
  let height;
  if (mask) {
    if (masksDefaultHeight.hasOwnProperty(mask.id)) {
      height = mask.height ?? masksDefaultHeight[mask.id]
    } else {
      // Pages pleine page, décor de page compris : toute la page.
      height = landscape ? pageWidth : pageHeight
    }
  }
  else height = container.height;
  const drawMode = mask || container.columnsIds.some((cid) => getColumnById(columns, cid)?.drawMode)
  const containerBoxes = mask ? [] : container.columnsIds.flatMap((cid) => getBoxesByColumnId(boxes, cid));
  return {
    height,
    mask,
    box: boxes.find((b) => container?.columnsIds.includes(b.columnId)),
    columnsIds: container?.columnsIds || [],
    marginLeft: configuration.margins.left * gridUnit,
    marginRight: configuration.margins.right * gridUnit,
    drawMode,
    hasBox: !mask && containerBoxes.length > 0 &&
      !(!drawMode && containerBoxes.some((b) => b.hideField)),
    currentDocument,
    landscape,
    fontFamily: configuration.fontFamily,
    fontSize: configuration.fontSize,
    selectedContainer,
    selectedContainerIds,
    activeContainerId,
    handleUnSelectBox,
    handleSelectContainer,
    handleUnSelectContainer,
    container: container,
    fromPdf,
    boxVariableSelectedId,
    zoomLevel,
    previewLock,
    // Ligne d'aperçu rendue comme un container : elle garde l'écart de son
    // container top-level.
    columnGap: containerColumnGap(
      container?.parentColumnId
        ? getTopLevelContainer({
            columnId: container.parentColumnId,
            containers,
            rowContainers,
          })
        : container
    ),
  }
};

const Container = React.memo(({ id, fromViewer }) => {
  const {
    columnsIds,
    marginLeft,
    marginRight,
    drawMode,
    height,
    mask,
    hasBox,
    landscape,
    fontFamily,
    fontSize,
    selectedContainer,
    selectedContainerIds,
    activeContainerId,
    handleUnSelectBox,
    handleSelectContainer,
    handleUnSelectContainer,
    container,
    fromPdf,
    boxVariableSelectedId,
    box,
    currentDocument,
    zoomLevel,
    previewLock,
    columnGap,
  } = useStore(selector(id, fromViewer));
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isResizingContainer, setIsResizingContainer] = useState(false);
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, setMenuIsOpen, !fromPdf);
  const [buttonsPosition, setButtonsPosition] = useState(null);
  const { overlayProps, updateLeftShownStatus } = useOverlay();
  const shownStatus = overlayProps?.left?.shownStatus;
  // Container "dessin" = toile libre : créé vide (cf. addBoxInColumn, seul
  // ItemTypes.DRAW n'insère aucune box) et rempli au glisser-déposer depuis la
  // palette. Les autres containers en drawMode (section, PDF, masque) portent
  // déjà leur propre box, on ne s'y glisse rien.
  const isDrawCanvas = drawMode && !mask && !isADrawBox(box?.type);

  const renderContent = () => {
    if (mask) return <ColumnWrapper
      id={mask.id}
      isContainerHovered={false}
      setIsResizingContainer={setIsResizingContainer}
      fromViewer={fromViewer}
    />
    return columnsIds.map((colId, index) => (
      <ColumnWrapper
        data-parent={containerBackground}
        id={colId}
        key={colId}
        isContainerHovered={isHovered}
        containerColumnsLength={columnsIds.length}
        setIsResizingContainer={setIsResizingContainer}
        fromViewer={fromViewer}
        isLastColumn={index === columnsIds.length - 1}
        isFirstColumn={index === 0}
        isMiddleColumn={index > 0 && index < columnsIds.length - 1}
      />
    ))
  }

  const stylecontainerSelected = useCallback(() => {
    if (fromPdf || fromViewer) {
      return {}
    }

    const selectedIds = selectedContainerIds?.length
      ? selectedContainerIds
      : selectedContainer?.id
        ? [selectedContainer.id]
        : [];
    const isSelected = selectedIds.includes(id);
    const isActive = activeContainerId === id || selectedContainer?.id === id;

    if (drawMode && !isSelected) {
      return {
        outline: `2px ${borderColumnColor} dotted`
      }
    }
    if (isSelected || drawMode) {
      return {
        outline: `2px #d7d9db ${drawMode ? "dotted" : "dashed"}`,
        boxShadow: isActive ? 'inset 0 0 0 1px #2166ab' : undefined,
      }
    }
    if (
      container?.sectionId &&
      selectedContainer?.sectionId &&
      (
        (container.fromBoxId && container.fromBoxId === selectedContainer.fromBoxId) ||
        (container.sectionId === selectedContainer.sectionId &&
          container.dateTimestamp === selectedContainer.dateTimestamp)
      )
    ) {
      return {
        outline: `2px #fce8d4 ${drawMode ? "dotted" : "dashed"}`
      }
    }
  }, [selectedContainer, selectedContainerIds, activeContainerId, id, container, drawMode, fromPdf, fromViewer, currentDocument])

  const containerRef = useRef(null);
  const handleContainerClick = useCallback(
    (event) => {
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      // Ignorer les clics issus des Select Ant Design (champ ou dropdown),
      // par exemple lors du choix d'une shape dans la navbar.
      if (
        event.target &&
        typeof event.target.closest === 'function' &&
        (event.target.closest('.ant-select') ||
          event.target.closest('.ant-select-dropdown'))
      ) {
        return;
      }
      // Idem pour les contrôles du panel de droite portés via
      // BoxFormControlsPortal (ex. switch "Afficher dans le formulaire") :
      // un portail reste un enfant React du container qui l'a monté, donc ce
      // handler `onClick` (attaché en React, qui bubble sur l'arbre React et
      // non le DOM) se déclenchait pour de vrai sur un clic dans le panel de
      // droite, désélectionnant la box qu'on est justement en train de régler.
      if (event.target?.closest?.('[data-outside-ignore="true"]')) {
        return;
      }
      if (fromViewer !== viewerTypes.PREVIEW) {
        const fromBoxClick = !!event.target?.closest?.(BOX_CLICK_SELECTOR);
        if (!fromBoxClick) {
          handleUnSelectBox('container_click');
          // Toile de dessin cliquée à vide : on ouvre la palette, seule chose à
          // faire ici est d'y glisser un élément. Exclu si le clic vient d'une
          // box (fromBoxClick) : on édite alors ce qui est déjà posé, changer
          // d'onglet ferait perdre le panneau en cours de consultation.
          if (isDrawCanvas) {
            updateLeftShownStatus(leftShownStatus.ELEMENTS);
          }
        }
        handleSelectContainer({
          containerId: id,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          fromBoxClick,
        });
      }
    },
    [fromViewer, handleSelectContainer, handleUnSelectBox, id, isDrawCanvas, updateLeftShownStatus]
  );

  const handleContainerMouseDownCapture = useCallback(
    (event) => {
      if (fromViewer === viewerTypes.PREVIEW) return;
      if (!(event.ctrlKey || event.metaKey || event.shiftKey)) return;
      if (drawMode) {
        const { selectedBoxId, boxes } = useStore.getState();
        const selectedBox = selectedBoxId ? boxes.find(b => b.id === selectedBoxId) : null;
        if (selectedBox && columnsIds.includes(selectedBox.columnId)) return;
      }
      event.preventDefault();
      if (window.getSelection) {
        window.getSelection()?.removeAllRanges?.();
      }
      handleSelectContainer({
        containerId: id,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
      });
    },
    [fromViewer, handleSelectContainer, id, drawMode, columnsIds]
  );

  useEffect(() => {
    if (fromPdf) return;
    function handleClickOutside(event) {
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      // Ne pas fermer si le clic est sur le portail des boutons (éditer / supprimer / verrou)
      if (event.target?.closest?.('[data-outside-ignore="true"]')) {
        return;
      }
      // Ce handler est monté par CHAQUE container. Un clic dans le container A
      // est « dehors » pour tous les autres, qui désélectionnaient donc A dès le
      // mousedown — avant que le click ne le resélectionne. Le panel de droite
      // se démontait et se remontait au passage, perdant son état (sections
      // dépliées). Un clic posé dans un container ne désélectionne rien : c'est
      // au handler de sélection de ce container-là de décider.
      if (event.target?.closest?.('.container')) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleUnSelectContainer();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleUnSelectContainer, fromPdf]);

  // Afficher les boutons dès que ce container est sélectionné (store). Une
  // box sélectionnée sélectionne aussi son container : la barre flottante
  // reste affichée dans ce cas (plus de barre flottante concurrente côté box,
  // ses actions vivent dans le panel de droite).
  const isMultiContainerSelection = (selectedContainerIds?.length || 0) > 1;
  // Afficher les boutons dès que ce container est sélectionné (store)
  const hasContainerOptions =
    !isMultiContainerSelection &&
    (menuIsOpen || (!mask && (activeContainerId === id || selectedContainer?.id === id))) &&
    shownStatus !== 'variables';

  useEffect(() => {
    if (!hasContainerOptions || !containerRef.current) {
      setButtonsPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setButtonsPosition({
        left: rect.left + rect.width / 2,
        top: rect.top - 50,
      });
    };

    updatePosition();

    // Gestion du scroll : le conteneur est dans un VList (Virtua) qui
    // scroll dans un élément avec la classe 'no-scrollbar'.
    const scrollContainer = document.querySelector('.no-scrollbar');

    window.addEventListener('resize', updatePosition);
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', updatePosition);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', updatePosition);
      }
    };
  }, [hasContainerOptions, zoomLevel]);

  // 2e passe: recentre horizontalement et recale verticalement selon la taille réelle
  // de la barre (connue seulement après montage), puis borne horizontalement pour
  // ne jamais empiéter sur les panels gauche/droite, et verticalement pour laisser
  // la place du "+" d'ajout de colonne au-dessus du container.
  useLayoutEffect(() => {
    if (!buttonsPosition || !containerRef.current || !wrapperRef.current) return
    const rect = containerRef.current.getBoundingClientRect();
    const { left: clampedLeft, top: adjustedTop } = computeClampedFloatyPosition({
      anchorTop: rect.top,
      anchorLeft: rect.left,
      anchorWidth: rect.width,
      toolbarWidth: wrapperRef.current.clientWidth,
      toolbarHeight: wrapperRef.current.clientHeight,
    })
    if (Math.abs(clampedLeft - buttonsPosition.left) > 0.5 || Math.abs(adjustedTop - buttonsPosition.top) > 0.5) {
      setButtonsPosition((prev) => ({ ...prev, left: clampedLeft, top: adjustedTop }))
    }
  }, [buttonsPosition])

  return <div id={id}
    className='container'
    ref={containerRef}
    onMouseDownCapture={handleContainerMouseDownCapture}
    onMouseOver={() => setIsHovered(true)}
    onMouseLeave={() => setIsHovered(false)}
    onClick={handleContainerClick}
    data-role={pageBackground}
    data-parent={containerBackground}
    style={{
      width: (landscape ? pageHeight : pageWidth),
      minHeight: hasBox || isResizingContainer ? "auto" : minContainerHeight,
      height: isResizingContainer ? "auto" : height,
      position: 'relative',
      fontSize: `${fontSize}px`,
      fontFamily: fontFamily,
      ...(previewLock ? { outline: `2px solid ${previewLock.color}` } : {}),
      ...(drawMode ? { overflow: 'hidden' } : {}),
    }}>
    {previewLock && <LockedBoxOverlay lock={previewLock} label="Aperçu par" />}
    <div
      data-role={pageBackground}
      data-parent={containerBackground}
      onClick={(event) => {
        event.stopPropagation();
        handleContainerClick(event);
      }}
      style={{
        width: (landscape ? pageHeight : pageWidth),
        minHeight: "inherit",
        height: "100%",
        display: "flex",
        paddingLeft: (drawMode || box?.type === ItemTypes.LINE_BREAK) ? 0 : marginLeft,
        paddingRight: (drawMode || box?.type === ItemTypes.LINE_BREAK) ? 0 : marginRight,
        columnGap,
        ...(stylecontainerSelected())
      }}>
      {renderContent()}
    </div>
    {hasContainerOptions && !isMultiContainerSelection && buttonsPosition && typeof document !== 'undefined' &&
      ReactDOM.createPortal(
        <FloatyStylingWrapper menuPosition={buttonsPosition} ref={wrapperRef}>
          <div data-outside-ignore="true" className='container-floaty-actions' style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RearangeColumnsButton id={id} menuIsOpen={menuIsOpen} setMenuIsOpen={setMenuIsOpen} />
            <NoRepeatContainerButton id={id} />
            <EditableContainerButton id={id} />
            {/* Sépare les actions strictement container (disposition, répétition,
                verrou) de copier/supprimer. Même séparateur que la barre de style
                texte. Les trois boutons ci-dessus rendent `null` selon le
                document (en proposal, souvent tous les trois) : le séparateur se
                masque alors tout seul, via `:first-child` (cf. index.less),
                plutôt qu'en recopiant ici leurs conditions d'affichage. */}
            <Separator />
            {/* containerOnly : cette barre flottante est celle du container, ses
                boutons visent toujours le container même si une box est
                sélectionnée dedans (cf. useBoxContainerActions). */}
            <BoxCopyDeleteButtons containerOnly />
          </div>
        </FloatyStylingWrapper>,
        document.body
      )
    }
  </div>
})

export default Container
